import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import {
  FaBoxOpen,
  FaCreditCard,
  FaFileInvoice,
  FaFileAlt,
  FaDownload,
  FaWarehouse,
  FaWrench,
} from "react-icons/fa";

import { fetchData } from "../helper/api.interceptor";
import "./customer-order-details.css";

const Customerorderdetails = () => {
  const { phone } = useParams();
  const [quotationData, setQuotationData] = useState(null);
  const [orderSummaryStatus, setOrderSummaryStatus] = useState("");
  const [processingDoc, setProcessingDoc] = useState(null);
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [stockDetails, setStockDetails] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  const [installationDocs, setInstallationDocs] = useState(null);
  const [loadingInstallationDocs, setLoadingInstallationDocs] = useState(false);

  useEffect(() => {
    const fetchQuotationDetails = async () => {
      try {
        const phone_number = decodeURIComponent(phone);

        const quotationRes = await fetchData.post(
          "/get-advance-paid-customer-details",
          { phone_number }
        );

        if (quotationRes.status_code === 200) {
          setQuotationData(quotationRes.result);
          setOrderSummaryStatus(
            quotationRes.result.order_summary?.status || ""
          );
        } else {
          Swal.fire("Error", "Failed to load quotation data", "error");
        }
      } catch (error) {
        console.error(error);
        Swal.fire("Error", "Something went wrong", "error");
      }
    };

    if (phone) fetchQuotationDetails();
  }, [phone]);

  // Fetch stock details when customer data is available
  useEffect(() => {
    const fetchStockDetails = async () => {
      if (!quotationData?.phone_number) return;

      setLoadingStock(true);
      try {
        const stockRes = await fetchData.post(
          "/customer-order-details",
          { customer_phone_number: quotationData.phone_number }
        );

        if (stockRes.status_code === 200) {
          setStockDetails(stockRes.result);
        } else {
          Swal.fire("Info", "Stock details not available", "info");
        }
      } catch (error) {
        console.error("Error fetching stock details:", error);
        Swal.fire("Error", "Failed to load stock details", "error");
      } finally {
        setLoadingStock(false);
      }
    };

    if (quotationData) {
      fetchStockDetails();
    }
  }, [quotationData]);

  // Fetch installation documents
  useEffect(() => {
    const fetchInstallationDocs = async () => {
      if (!quotationData?.phone_number) return;

      setLoadingInstallationDocs(true);
      try {
        console.log(quotationData);

        const projectRes = await fetchData.post(
          "/file-manager/project-details/admin",
          { phone_number: quotationData.phone_number }
        );

        if (projectRes.status_code === 200) {
          setInstallationDocs(projectRes.result.installation_docs);
        }
      } catch (error) {
        console.error("Error fetching installation docs:", error);
      } finally {
        setLoadingInstallationDocs(false);
      }
    };

    if (quotationData) {
      fetchInstallationDocs();
    }
  }, [quotationData]);

  // ------------------------ STATUS UPDATE HANDLER -----------------------

  const handleStatusUpdate = async () => {
    if (!orderSummaryStatus) return;

    const formData = new FormData();
    formData.append("phone_number", quotationData.phone_number);
    formData.append("status", orderSummaryStatus);

    if (orderSummaryStatus === "Customer Sign/Invoice" && processingDoc) {
      formData.append("document", processingDoc);
    }

    try {
      const res = await fetchData.postWithFormData(
        "/order/status",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (res.status_code === 200) {
        Swal.fire("Success", "Status updated successfully", "success");

        setQuotationData((prev) => ({
          ...prev,
          order_summary: {
            ...prev.order_summary,
            status: orderSummaryStatus,
          },
        }));
      } else {
        Swal.fire("Error", res.message || "Failed to update status", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  // ------------------------ DOCUMENT STATUS UPDATE -----------------------

  const updateDocumentStatus = async (type, status) => {
    try {
      const response = await fetchData.post("/update-doc-status", {
        phone_number: quotationData.phone_number,
        type,
        status,
      });

      if (response.status_code === 200) {
        Swal.fire("Success", `Document ${status} successfully`, "success");

        setQuotationData((prev) => {
          const updatedDocs = { ...prev.documents };
          updatedDocs[type].status = status;
          return { ...prev, documents: updatedDocs };
        });
      } else {
        Swal.fire("Error", response.message || "Failed to update", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  // ------------------------ INSTALLATION DOCUMENT STATUS UPDATE -----------------------

  const updateInstallationDocStatus = async (documentName, status) => {
    try {
      const response = await fetchData.post("/update-installation-doc-status", {
        customer_phone_number: quotationData.phone_number,
        document_name: documentName,
        status: status,
      });

      if (response.status_code === 200) {
        Swal.fire("Success", `Installation document ${status} successfully`, "success");

        // Update local state
        setInstallationDocs(prev => ({
          ...prev,
          [documentName]: {
            ...prev[documentName],
            status: status
          }
        }));
      } else {
        Swal.fire("Error", response.message || "Failed to update", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  const customerData = quotationData;
  const orderSummary = quotationData?.order_summary;
  const documentData = quotationData?.documents;

  return (
    <div className="customer-order-details">

      {/* ---------- HEADER ---------- */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <h6 className="fw-semibold mb-0">Customers Order Details</h6>
        <ul className="d-flex align-items-center gap-2">
          <li className="fw-medium">
            <Link to="/customers" className="d-flex align-items-center gap-1 hover-text-primary">
              <Icon icon="solar:home-smile-angle-outline" className="icon text-lg" />
              Customers
            </Link>
          </li>
          <li> - </li>
          <li className="fw-medium">Order Summary</li>
        </ul>
      </div>

      <div className="row gy-2">

        {/* ---------- PERSONAL DETAILS ---------- */}
        <div className="col-12">
          <div className="customer-detail-box p-20 shadow-sm">

            <h5 className="mb-3 d-flex align-items-center gap-2">
              <Icon icon="iconamoon:profile-circle-light" /> | Personal Details
            </h5>

            {customerData ? (
              <div className="row">
                {[
                  { label: "Name", value: customerData.personal_details?.user_name },
                  { label: "Phone", value: customerData.phone_number },
                  { label: "Email", value: customerData.personal_details?.email },
                  { label: "Project Status", value: customerData.project_status },
                  { label: "State", value: customerData?.address?.state },
                  { label: "District", value: customerData?.address?.district },
                  { label: "City/Village", value: customerData?.address?.city_or_village },
                  { label: "Pincode", value: customerData?.address?.pincode },
                ].map((item, idx) => (
                  <div className="col-md-3" key={idx}>
                    <h6 className="mb-1">{item.label}:</h6>
                    <p className="mb-3">{item.value || "-"}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p>Loading personal details...</p>
            )}
          </div>
        </div>

        {/* ---------- ORDER SUMMARY ---------- */}
        <div className="col-lg-6">
          <div className="customer-detail-box p-20 shadow-sm h-100 position-relative">

            {/* Customer Sign Document Download */}
            {orderSummary?.customersigndoc && (
              <div className="position-absolute top-0 end-0 text-end custom_padding">
                <label className="fw-semibold mb-0">Customer Sign Document:</label>
                <FaDownload
                  size={18}
                  className="text-primary"
                  style={{ cursor: "pointer" }}
                  title="Download"
                  onClick={() => {
                    const basePath = process.env.REACT_APP_UPLOAD_PATH;
                    const filePath = orderSummary.customersigndoc;
                    const fileUrl = `${basePath}${filePath}`;

                    fetch(fileUrl)
                      .then((res) => res.blob())
                      .then((blob) => {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = filePath.split("/").pop();
                        a.click();
                        URL.revokeObjectURL(url);
                      });
                  }}
                />
              </div>
            )}

            <h5 className="mb-4 d-flex align-items-center gap-2 fs-5">
              <FaBoxOpen className="text-success fs-4" /> | Order Summary
            </h5>

            {orderSummary ? (
              <div className="order-summary-content">

                <div className="mb-3">
                  <span className="text-muted fw-semibold">Order ID:</span>
                  <span className="d-block fs-6 fw-bold">{orderSummary.order_id || "-"}</span>
                </div>

                <div className="mb-3">
                  <span className="text-muted fw-semibold">Reading Date:</span>
                  <span className="d-block fs-6 fw-bold">
                    {new Date(orderSummary.reading_date).toLocaleDateString()}
                  </span>
                </div>

                <div className="mb-3">
                  <span className="text-muted fw-semibold">Panel:</span>
                  <span className="d-block fs-6 fw-bold">
                    {orderSummary.panel_brand} - ₹
                    {orderSummary.panel_price?.toLocaleString("en-IN")}
                  </span>
                </div>

                <div className="mb-3">
                  <span className="text-muted fw-semibold">Inverter:</span>
                  <span className="d-block fs-6 fw-bold">
                    {orderSummary.inverter_brand} - ₹
                    {orderSummary.inverter_price?.toLocaleString("en-IN")}
                  </span>
                </div>

                {/* STATUS UPDATE */}
                <div className="mb-3">
                  <span className="text-muted fw-semibold">Status:</span>

                  <select
                    className="form-select mt-2"
                    value={orderSummaryStatus}
                    onChange={(e) => setOrderSummaryStatus(e.target.value)}
                  >
                    {[
                      "Pending",
                      "Customer Sign/Invoice",
                      "Structure work",
                      "Panel Fitting",
                      "Electric work",
                      "Invertor Fitting",
                      "Complete Install meter",
                      "Bill Generate Signature",
                    ].map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>

                  {orderSummaryStatus === "Customer Sign/Invoice" && (
                    <div className="mt-2">
                      <label className="form-label">Upload Customer Sign/Invoice Document</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) => setProcessingDoc(e.target.files[0])}
                      />
                    </div>
                  )}

                  <button
                    className="btn btn-sm btn-primary mt-3"
                    style={{
                      width: "150px",
                      backgroundColor: "#e4d838ff",
                      color: "black",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                    onClick={handleStatusUpdate}
                  >
                    Update Status
                  </button>
                </div>

              </div>
            ) : (
              <p>Loading order summary...</p>
            )}
          </div>
        </div>

        {/* ---------- PAYMENT SUMMARY ---------- */}
        <div className="col-lg-6">
          <div className="customer-detail-box p-20 shadow-sm h-100">
            <h5 className="mb-3 d-flex align-items-center gap-2">
              <FaCreditCard className="text-danger fs-4" /> | Payment Summary
            </h5>

            {quotationData?.payment ? (
              <ul className="quotation-payment list-unstyled">

                <li className="d-flex justify-content-between border-bottom pb-1">
                  <span className="text-muted">Panel Price</span>
                  <span className="fw-bold">
                    ₹{quotationData.order_summary?.panel_price?.toLocaleString("en-IN")}
                  </span>
                </li>

                <li className="d-flex justify-content-between border-bottom pb-1">
                  <span className="text-muted">Inverter Price</span>
                  <span className="fw-bold">
                    ₹{quotationData.order_summary?.inverter_price?.toLocaleString("en-IN")}
                  </span>
                </li>

                <li className="d-flex justify-content-between border-bottom pb-1">
                  <span className="text-muted">Other System Cost</span>
                  <span className="fw-bold">
                    ₹
                    {(
                      quotationData.payment.total_cost -
                      quotationData.order_summary.panel_price -
                      quotationData.order_summary.inverter_price
                    ).toLocaleString("en-IN")}
                  </span>
                </li>

                <li className="d-flex justify-content-between border-bottom pb-1">
                  <span className="text-muted">Total System Cost</span>
                  <span className="fw-bold">
                    ₹{quotationData.payment.total_cost?.toLocaleString("en-IN")}
                  </span>
                </li>

                <li className="d-flex justify-content-between border-bottom pb-1">
                  <span className="text-muted">Subsidy</span>
                  <span className="fw-bold text-success">
                    ₹{quotationData.payment.subsidy?.toLocaleString("en-IN")}
                  </span>
                </li>

                {quotationData.payment.discount > 0 && (
                  <li className="d-flex justify-content-between border-bottom pb-1">
                    <span className="text-muted">Discount</span>
                    <span className="fw-bold text-success">
                      ₹{quotationData.payment.discount?.toLocaleString("en-IN")}
                    </span>
                  </li>
                )}

                <li className="d-flex justify-content-between border-bottom pb-1">
                  <span className="text-muted">Payable Amount</span>
                  <span className="fw-bold text-primary">
                    ₹{quotationData.payment.payable_amount?.toLocaleString("en-IN")}
                  </span>
                </li>

                {/* PAYMENT STATUS SUMMARY */}
                <div className="mt-3">
                  <h6 className="fw-semibold mb-2">Payment Status</h6>
                  <ul className="quotation-payment list-unstyled">
                    <li className="d-flex justify-content-between border-bottom pb-1">
                      <span className="text-muted">Total Received Amount</span>
                      <span className="fw-bold text-success">
                        ₹{quotationData.payment.total_received_amount?.toLocaleString("en-IN")}
                      </span>
                    </li>

                    <li className="d-flex justify-content-between border-bottom pb-1">
                      <span className="text-muted">Total Pending Amount</span>
                      <span className="fw-bold text-danger">
                        ₹{quotationData.payment.total_pending_amount?.toLocaleString("en-IN")}
                      </span>
                    </li>

                    <li className="d-flex justify-content-between border-bottom pb-1">
                      <span className="text-muted">Order Payment Status</span>
                      <span className="fw-bold text-primary">
                        {quotationData.payment.order_payment_status}
                      </span>
                    </li>
                  </ul>
                </div>

                {/* INSTALLMENT DETAILS */}
                <div className="mt-3">
                  <h6 className="fw-semibold mb-2">Installment Details</h6>

                  {quotationData.payment.installments?.length > 0 ? (
                    <div className="installment-list">
                      {quotationData.payment.installments.map((installment, index) => (
                        <div key={index} className="installment-item mb-3 p-3 border rounded">
                          <h6 className="fw-semibold mb-2">Installment {index + 1}</h6>

                          <ul className="list-unstyled mb-0">
                            <li className="d-flex justify-content-between border-bottom pb-1">
                              <span className="text-muted">Amount</span>
                              <span className="fw-bold">
                                ₹{(installment.amount)?.toLocaleString("en-IN")}
                              </span>
                            </li>

                            <li className="d-flex justify-content-between border-bottom pb-1">
                              <span className="text-muted">Payment Method</span>
                              <span className="fw-bold text-capitalize">
                                {installment.payment_method}
                              </span>
                            </li>

                            <li className="d-flex justify-content-between border-bottom pb-1">
                              <span className="text-muted">Status</span>
                              <span className={`fw-bold ${installment.status === 'paid' ? 'text-success' :
                                installment.status === 'pending' ? 'text-warning' : 'text-danger'
                                }`}>
                                {installment.status?.charAt(0).toUpperCase() + installment.status?.slice(1)}
                              </span>
                            </li>

                            <li className="d-flex justify-content-between border-bottom pb-1">
                              <span className="text-muted">Received Date</span>
                              <span className="fw-bold">
                                {installment.received_date
                                  ? new Date(installment.received_date).toLocaleDateString("en-IN")
                                  : "-"}
                              </span>
                            </li>

                            {installment.payment_order_id && (
                              <li className="d-flex justify-content-between border-bottom pb-1">
                                <span className="text-muted">Order ID</span>
                                <span className="fw-bold">{installment.payment_order_id}</span>
                              </li>
                            )}

                            {installment.reference_number && (
                              <li className="d-flex justify-content-between border-bottom pb-1">
                                <span className="text-muted">Reference No</span>
                                <span className="fw-bold">{installment.reference_number}</span>
                              </li>
                            )}

                            <li className="d-flex justify-content-between border-bottom pb-1">
                              <span className="text-muted">Received By</span>
                              <span className="fw-bold text-capitalize">
                                {installment.received_by?.replace(/-/g, ' ') || "-"}
                              </span>
                            </li>

                            {installment.isPaymentDoneByCustomer && (
                              <li className="d-flex justify-content-between">
                                <span className="text-muted">Payment Type</span>
                                <span className="fw-bold text-success">Online Payment</span>
                              </li>
                            )}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No installment details available</p>
                  )}
                </div>

              </ul>
            ) : (
              <p>Loading payment details...</p>
            )}
          </div>
        </div>


        {/* ---------- CUSTOMER DOCUMENTS SECTION ---------- */}
        <div className="col-lg-6">
          <div className="customer-detail-box p-20 shadow-sm h-100">
            <h5 className="mb-3 d-flex align-items-center gap-2">
              <FaFileAlt className="text-info fs-4" /> | Customer Documents
            </h5>

            {documentData ? (
              <table className="table mb-0 text-center">
                <thead>
                  <tr>
                    <th className="text-center">Document</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Action</th>
                    <th className="text-center">Download</th>
                  </tr>
                </thead>

                <tbody>
                  {[
                    { name: "Aadhar Card", data: documentData.aadhar_card },
                    { name: "PAN Card", data: documentData.pan_card },
                    { name: "Electricity Bill", data: documentData.electricity_bill },
                    { name: "Bank Passbook", data: documentData.bank_passbook },
                  ].map((doc, idx) => (
                    <tr key={idx}>
                      <td>{doc.name}</td>

                      <td>
                        <span
                          className={
                            doc.data.status === "Verified"
                              ? "text-success"
                              : doc.data.status === "Under Review"
                                ? "text-warning"
                                : "text-danger"
                          }
                        >
                          {doc.data.status}
                        </span>
                      </td>

                      <td>
                        {doc.data.status === "Under Review" ? (
                          <div className="d-flex gap-2 justify-content-center">
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() =>
                                updateDocumentStatus(
                                  doc.name.toLowerCase().replace(/\s/g, "_"),
                                  "Verified"
                                )
                              }
                            >
                              Approve
                            </button>

                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() =>
                                updateDocumentStatus(
                                  doc.name.toLowerCase().replace(/\s/g, "_"),
                                  "Rejected"
                                )
                              }
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td>
                        <div className="d-flex justify-content-center gap-2">

                          {/* VIEW */}
                          <button
                            className="btn btn-light border rounded-circle p-2"
                            onClick={() => {
                              const basePath = process.env.REACT_APP_UPLOAD_PATH;
                              const filePath = doc.data.url;
                              window.open(`${basePath}${filePath}`, "_blank");
                            }}
                          >
                            <Icon icon="iconamoon:eye-light" />
                          </button>

                          {/* DOWNLOAD */}
                          <button
                            className="btn btn-light border rounded-circle p-2"
                            onClick={() => {
                              const basePath = process.env.REACT_APP_UPLOAD_PATH;
                              const filePath = doc.data.url;

                              fetch(`${basePath}${filePath}`)
                                .then((res) => res.blob())
                                .then((blob) => {
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.download = filePath.split("/").pop();
                                  a.click();
                                  URL.revokeObjectURL(url);
                                });
                            }}
                          >
                            <Icon icon="mdi:download" />
                          </button>

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>

              </table>
            ) : (
              <p>Loading documents...</p>
            )}
          </div>
        </div>

        {/* ---------- INSTALLATION DOCUMENTS SECTION ---------- */}
        <div className="col-lg-6">
          <div className="customer-detail-box p-20 shadow-sm h-100">
            <h5 className="mb-3 d-flex align-items-center gap-2">
              <FaWrench className="text-primary fs-4" /> | Installation Documents
            </h5>

            {loadingInstallationDocs ? (
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading installation documents...</p>
              </div>
            ) : installationDocs ? (
              <table className="table mb-0 text-center">
                <thead>
                  <tr>
                    <th className="text-center">Document</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Action</th>
                    <th className="text-center">Download</th>
                  </tr>
                </thead>

                <tbody>
                  {Object.entries(installationDocs).map(([docName, docData], idx) => (
                    <tr key={idx}>
                      <td>{docName}</td>

                      <td>
                        <span
                          className={
                            docData.status === "Verified"
                              ? "text-success"
                              : docData.status === "Under Review"
                                ? "text-warning"
                                : "text-danger"
                          }
                        >
                          {docData.status}
                        </span>
                      </td>

                      <td>
                        {docData.status === "Under Review" ? (
                          <div className="d-flex gap-2 justify-content-center">
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() =>
                                updateInstallationDocStatus(docName, "Verified")
                              }
                            >
                              Approve
                            </button>

                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() =>
                                updateInstallationDocStatus(docName, "Rejected")
                              }
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td>
                        <div className="d-flex justify-content-center gap-2">

                          {/* VIEW */}
                          <button
                            className="btn btn-light border rounded-circle p-2"
                            onClick={() => {
                              const basePath = process.env.REACT_APP_UPLOAD_PATH;
                              const filePath = docData.file_path;
                              window.open(`${basePath}${filePath}`, "_blank");
                            }}
                          >
                            <Icon icon="iconamoon:eye-light" />
                          </button>

                          {/* DOWNLOAD */}
                          <button
                            className="btn btn-light border rounded-circle p-2"
                            onClick={() => {
                              const basePath = process.env.REACT_APP_UPLOAD_PATH;
                              const filePath = docData.file_path;

                              fetch(`${basePath}${filePath}`)
                                .then((res) => res.blob())
                                .then((blob) => {
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.download = filePath.split("/").pop();
                                  a.click();
                                  URL.revokeObjectURL(url);
                                });
                            }}
                          >
                            <Icon icon="mdi:download" />
                          </button>

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>

              </table>
            ) : (
              <p className="text-muted">No installation documents available.</p>
            )}
          </div>
        </div>
        {/* ---------- STOCK/INVENTORY DETAILS ---------- */}
        <div className="col-12">
          <div className="customer-detail-box p-20 shadow-sm">
            <h5 className="mb-3 d-flex align-items-center gap-2">
              <FaWarehouse className="text-warning fs-4" /> | Assigned Stock Details
            </h5>

            {loadingStock ? (
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading stock details...</p>
              </div>
            ) : stockDetails ? (
              <div>
                {/* <div className="row mb-4">
                  <div className="col-md-4">
                    <h6 className="text-muted">Customer Name</h6>
                    <p className="fw-bold">{stockDetails.customer_name || "-"}</p>
                  </div>
                  <div className="col-md-4">
                    <h6 className="text-muted">Order ID</h6>
                    <p className="fw-bold">{stockDetails.order_id || "-"}</p>
                  </div>
                  <div className="col-md-4">
                    <h6 className="text-muted">Delivery Status</h6>
                    <p className="fw-bold text-capitalize">{stockDetails.delivery_status || "-"}</p>
                  </div>
                  <div className="col-md-12">
                    <h6 className="text-muted">Address</h6>
                    <p className="fw-bold">{stockDetails.address || "-"}</p>
                  </div>
                </div> */}

                <h6 className="mb-3 fw-semibold">Inventory Items</h6>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Product</th>
                        <th>Stock Assigned</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockDetails.inventory?.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <strong>{item.show_name}</strong>
                          </td>
                          <td>
                            <span className={`badge ${item.total_number_of_stock > 0 ? 'bg-success' : 'bg-danger'}`}>
                              {item.total_number_of_stock} units
                            </span>
                          </td>
                          <td>
                            {item.solar_list?.length > 0 && (
                              <div className="mt-1">
                                <small className="text-muted">Solar Panels:</small>
                                <ul className="mb-0 ps-3">
                                  {item.solar_list.map((solar, sIdx) => (
                                    <li key={sIdx}>
                                      {solar.dcr_number}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {item.inverter_list?.length > 0 && (
                              <div className="mt-1">
                                <small className="text-muted">Inverters:</small>
                                <ul className="mb-0 ps-3">
                                  {item.inverter_list.map((inv, iIdx) => (
                                    <li key={iIdx}>
                                      {inv.inverter_serial}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {item.acdc_list?.length > 0 && (
                              <div className="mt-1">
                                <small className="text-muted">ACDC Boxes:</small>
                                <ul className="mb-0 ps-3">
                                  {item.acdc_list.map((acdc, aIdx) => (
                                    <li key={aIdx}>
                                      {acdc.acdcbox_details}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-muted">No stock details available for this customer.</p>
            )}
          </div>
        </div>


        {/* ---------- QUOTATION BREAKDOWN ---------- */}
        <div className="col-lg-6">
          <div className="customer-detail-box p-20 shadow-sm h-100">
            <h5 className="mb-3 d-flex align-items-center gap-2">
              <FaFileInvoice className="text-warning fs-4" /> | Quotation Breakdown
            </h5>

            {quotationData?.quotation_breakdown?.length > 0 ? (
              <div>
                {quotationData.quotation_breakdown.map((item, idx) => (
                  <p key={idx}>
                    <strong>{item.title}:</strong> ₹
                    {item.amount.toLocaleString("en-IN")}
                  </p>
                ))}
              </div>
            ) : (
              <p>No breakdown available</p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Customerorderdetails;